import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { postJSON } from '../../api';

type CaptchaInitResponse = {
  captchaId: string;
  expiresAt: string;
  width: number;
  height: number;
  piece: { y: number; w: number; h: number };
  bgDataUrl: string;
};

type CaptchaVerifyResponse = {
  captchaToken: string;
  expiresAt: string;
};

function SliderCaptchaModal(props: {
  open: boolean;
  mobile: string;
  countryCode: string;
  onClose: () => void;
  onSuccess: (token: string) => void;
}) {
  const { open, mobile, countryCode, onClose, onSuccess } = props;

  const [loading, setLoading] = useState(false);
  const [init, setInit] = useState<CaptchaInitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [x, setX] = useState(0);
  const startAtRef = useRef<number | null>(null);

  const maxX = useMemo(() => {
    if (!init) return 0;
    return Math.max(0, init.width - init.piece.w);
  }, [init]);

  const refresh = async () => {
    setError(null);
    setInit(null);
    setX(0);
    startAtRef.current = null;
    setLoading(true);
    try {
      const res = await postJSON<CaptchaInitResponse>({
        url: 'user/captcha/init',
        body: { scene: 'sms_code', mobile, countryCode }
      });
      setInit(res);
    } catch (e: any) {
      setError(e?.message ?? '验证码初始化失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const verify = async () => {
    if (!init) return;
    setError(null);
    setLoading(true);
    try {
      const durationMs = startAtRef.current ? Date.now() - startAtRef.current : undefined;
      const res = await postJSON<CaptchaVerifyResponse>({
        url: 'user/captcha/verify',
        body: {
          scene: 'sms_code',
          captchaId: init.captchaId,
          x,
          durationMs,
          mobile,
          countryCode,
        }
      });
      onSuccess(res.captchaToken);
    } catch (e: any) {
      setError(e?.message ?? '验证失败，请重试');
      // 失败后自动刷新一次 challenge，避免重复尝试同一题
      await refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ pr: 6 }}>
        安全验证
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          请拖动滑块完成验证
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              width: init?.width ?? 320,
              borderRadius: 1,
              overflow: 'hidden',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.default',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: init?.width ?? 320,
                height: init?.height ?? 160,
              }}
            >
            {init?.bgDataUrl ? (
              <Box
                component="img"
                src={init.bgDataUrl}
                alt="captcha"
                sx={{
                  display: 'block',
                  width: init.width,
                  height: init.height,
                }}
              />
            ) : (
              <Box
                sx={{
                  height: 160,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                {loading ? <CircularProgress size={20} /> : '加载失败'}
              </Box>
            )}
            {init && (
              <Box
                sx={{
                  position: 'absolute',
                  top: init.piece.y,
                  left: 0,
                  width: init.piece.w,
                  height: init.piece.h,
                  borderRadius: 1,
                  border: '2px solid rgba(255,255,255,0.8)',
                  bgcolor: 'rgba(0,0,0,0.06)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                  transform: `translateX(${x}px)`,
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>

          <Box sx={{ px: 2, py: 1.5, width: init?.width ?? 320 }}>
            <Slider
              value={x}
              min={0}
              max={maxX}
              step={1}
              disabled={!init || loading}
              onChange={(_, value) => {
                if (!startAtRef.current) startAtRef.current = Date.now();
                setX(Array.isArray(value) ? value[0] : value);
              }}
              onChangeCommitted={async () => {
                await verify();
              }}
              aria-label="captcha-slider"
            />
          </Box>
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={refresh} disabled={loading}>
          换一张
        </Button>
        <Button onClick={onClose} disabled={loading}>
          取消
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export async function requestSmsCaptchaToken(params: {
  mobile: string;
  countryCode: string;
}): Promise<string> {
  const mobile = (params.mobile || '').trim();
  const { countryCode } = params;

  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root: Root = createRoot(container);

    const cleanup = () => {
      try {
        root.unmount();
      } finally {
        container.remove();
      }
    };

    const handleClose = () => {
      cleanup();
      reject(new Error('用户取消验证'));
    };

    const handleSuccess = (token: string) => {
      cleanup();
      resolve(token);
    };

    root.render(
      <SliderCaptchaModal
        open
        mobile={mobile}
        countryCode={countryCode}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    );
  });
}
